import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet';
import { useAuth } from '../context/AuthContext';
import {
  Stethoscope,
  Bell,
  Mail,
  CheckCircle2,
  MapPin,
  Briefcase,
  ArrowRight,
  ChevronRight,
  Search,
  Filter,
  Zap,
  Clock,
  Smartphone,
  ExternalLink,
} from 'lucide-react';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: Mail,
      title: 'Instant Unlimited Email Alerts',
      description: 'Get unlimited email notifications for every new job that matches your criteria - no limits, no caps.',
    },
    {
      icon: Smartphone,
      title: 'Manage Preferences Anywhere',
      description: 'Install our app on iOS, Android & Desktop to easily manage your alerts, filters, and notification settings.',
    },
    {
      icon: ExternalLink,
      title: 'Direct Job Links',
      description: 'One tap takes you straight to the job on healthjobsuk.com - apply instantly.',
    },
    {
      icon: MapPin,
      title: 'All UK Jobs',
      description: 'Filter by region, city, or specific NHS Trusts to find roles near you.',
    },
    {
      icon: Briefcase,
      title: 'Doctor-Specific Filters',
      description: 'Search by grade (FY1, FY2, CT, ST, Clinical Fellow, Trust Doctor) and specialty.',
    },
    {
      icon: Bell,
      title: 'Never Miss a Job',
      description: 'We monitor 24/7 and alert you immediately via email when new jobs are posted.',
    },
  ];

  const howItWorks = [
    {
      step: '1',
      icon: Filter,
      title: 'Set Your Filters',
      description: 'Choose your preferred grade, specialty, and location. FY1? SHO? Clinical Fellow? We\'ve got you covered.',
    },
    {
      step: '2',
      icon: Search,
      title: 'We Monitor 24/7',
      description: 'Our system checks 24/7 for new vacancies matching your criteria.',
    },
    {
      step: '3',
      icon: Zap,
      title: 'Get Instant Alerts',
      description: 'New job posted? Get email alerts within minutes with direct links to apply.',
    },
  ];

  const stats = [
    { value: '5 min', label: 'Alerted Within' },
    { value: '24/7', label: 'Monitoring' },
    { value: '100%', label: 'NHS Jobs' },
    { value: '£9.99', label: 'Per Month' },
  ];

  return (
    <>
      <Helmet>
        <title>NHS Doctor Jobs for International Medical Graduates | TRACJobsAlert</title>
        <meta name="title" content="NHS Doctor Jobs for International Medical Graduates | TRACJobsAlert" />
        <meta name="description" content="Get instant NHS job alerts for IMGs. Find Trust Grade, LED, and Staff Grade doctor vacancies across the UK. Build the NHS experience you need under the 2026 Medical Training Prioritisation Act." />
        <meta name="keywords" content="NHS doctor jobs for IMGs, IMG doctor jobs UK, international medical graduate NHS job UK, NHS job alerts for doctors, Trust Grade doctor jobs UK, Locally Employed Doctor jobs UK, LED jobs NHS IMG, TRAC jobs NHS doctor, Medical Training Prioritisation Act IMG, IMG NHS experience" />
        <link rel="canonical" href="https://tracjobsalert.co.uk/" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tracjobsalert.co.uk/" />
        <meta property="og:title" content="NHS Doctor Job Alerts for IMGs | TRACJobsAlert" />
        <meta property="og:description" content="Real-time NHS job alerts built for international medical graduates. Apply to LED and Trust Grade roles before anyone else." />
        <meta property="og:image" content="https://tracjobsalert.co.uk/icon-512.png" />
        <meta property="og:site_name" content="TRACJobsAlert" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://tracjobsalert.co.uk/" />
        <meta name="twitter:title" content="NHS Doctor Job Alerts for IMGs | TRACJobsAlert" />
        <meta name="twitter:description" content="Real-time NHS job alerts for international medical graduates. Find LED, Trust Grade & Staff Grade doctor roles across the UK." />
        <meta name="twitter:image" content="https://tracjobsalert.co.uk/icon-512.png" />
      </Helmet>
      
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-16">
            <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="logo">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="font-bold text-sm sm:text-lg text-slate-900 leading-tight truncate">Trac Jobs Alert</span>
                <span className="text-xs text-emerald-600 hidden sm:block">For Doctors</span>
              </div>
            </Link>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4 sm:px-6 text-sm sm:text-base" data-testid="dashboard-btn">
                    Dashboard
                    <ArrowRight className="w-4 h-4 ml-1 sm:ml-2 hidden sm:inline" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 rounded-full px-3 sm:px-6 text-sm sm:text-base font-medium" data-testid="login-btn">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-3 sm:px-6 text-sm sm:text-base font-medium" data-testid="get-started-btn">
                      <span className="hidden sm:inline">Get Started</span>
                      <span className="sm:hidden">Start</span>
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Urgency Banner - Medical Training Prioritisation Act */}
      <div className="bg-amber-500 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <span className="text-lg">⚠️</span>
          <p className="text-sm sm:text-base font-medium">
            <strong>Act Now:</strong> The Medical Training Prioritisation Act 2026 means IMGs must build NHS experience urgently to qualify for specialty training.
            <Link to="/img-guide/medical-training-prioritisation-act-2026" className="underline ml-2 hover:text-amber-100">
              Learn more →
            </Link>
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-700 text-sm font-medium mb-4">
            <Stethoscope className="w-4 h-4 mr-2" />
            Built specifically for International Medical Graduates
          </div>
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-sm font-medium mb-8">
            <Clock className="w-4 h-4 mr-2" />
            LED & Trust Grade posts fill within 48 hours - get alerted instantly
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
            NHS Doctor Job Alerts for{' '}
            <span className="text-emerald-500">International Medical Graduates</span>
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed mb-8 max-w-3xl mx-auto">
            Find <strong>Trust Grade, LED, Staff Grade & Clinical Fellow</strong> positions across the UK. 
            Build the NHS experience you need under the 2026 Medical Training Prioritisation Act. 
            Get instant alerts when new IMG-friendly roles are posted.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/register">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg" data-testid="hero-cta-btn">
                Get Alerts Now - £9.99/month
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span>Trust Grade & LED roles</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span>IMG-friendly positions</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span>Visa sponsorship roles</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span>Cancel anytime</span>
            </div>
          </div>
          
          {/* App Availability Badge */}
          <div className="mt-10 flex flex-col items-center">
            <p className="text-slate-500 text-sm mb-3">Manage your alerts on any device</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 border border-slate-200">
                <svg className="w-5 h-5 text-slate-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span className="text-slate-600 text-sm font-medium">iOS</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 border border-slate-200">
                <svg className="w-5 h-5 text-slate-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.523 2.047a.5.5 0 0 0-.27.103l-8.86 6.165a.5.5 0 0 0 0 .813l2.21 1.54-2.21 1.54a.5.5 0 0 0 0 .812l8.86 6.165a.5.5 0 0 0 .77-.406V2.453a.5.5 0 0 0-.5-.406zM6.5 8.5a.5.5 0 0 0-.277.084l-4 2.75a.5.5 0 0 0 0 .832l4 2.75A.5.5 0 0 0 7 14.5v-5.5a.5.5 0 0 0-.5-.5z"/>
                </svg>
                <span className="text-slate-600 text-sm font-medium">Android</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 border border-slate-200">
                <svg className="w-5 h-5 text-slate-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8h16v10z"/>
                </svg>
                <span className="text-slate-600 text-sm font-medium">Desktop</span>
              </div>
            </div>
            <p className="text-slate-500 text-xs mt-2">Install as app • Manage preferences • Unlimited email alerts</p>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-12 bg-red-50 border-y border-red-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-red-600 font-medium">
            "I saw a perfect job on trac.jobs but by the time I applied, it was already filled..."
          </p>
          <p className="text-slate-500 mt-2">- Sound familiar? You're not alone.</p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Simple setup, instant results. Be the first to know about new medical jobs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-16 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-emerald-500/50 via-emerald-500 to-emerald-500/50" />
            
            {howItWorks.map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500 text-white text-2xl font-bold mb-6 relative z-10">
                  {item.step}
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                  <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-emerald-500" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-emerald-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-emerald-100 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Built for Doctors, By Design
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We understand the NHS job market. Our filters match exactly what you see on healthjobsuk.com.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-emerald-500/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Preview Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Filter Jobs Your Way
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Set up your perfect job search with filters that match exactly what you're looking for. Select your grade, specialty, and preferred locations.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Junior doctor grades: FY1 to ST5</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Multiple specialties and locations</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Keyword search for specific roles</span>
                </li>
              </ul>
            </div>
            
            {/* Mockup Preview */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Grade</label>
                  <div className="relative">
                    <select className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-700 appearance-none cursor-pointer" disabled>
                      <option>Foundation Year 1 (FY1)</option>
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 rotate-90" />
                  </div>
                  {/* Dropdown Preview */}
                  <div className="mt-2 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                    <div className="py-2">
                      {['Foundation Year 1 (FY1)', 'Foundation Year 2 (FY2)', 'Core Training (CT1-2)', 'Specialty Training (ST1-5)', 'Middle Grade (MT01-MT05)', 'Specialty Doctor', 'Staff Grade', 'Clinical Fellow'].map((grade, i) => (
                        <div key={i} className={`px-4 py-2 text-sm ${i === 0 ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'}`}>
                          {grade}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                  <div className="relative">
                    <select className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-700 appearance-none" disabled>
                      <option>All Locations</option>
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 rotate-90" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Keywords</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Cardiology, Paediatrics..." 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-500"
                    disabled
                  />
                </div>
                
                <button className="w-full bg-emerald-500 text-white py-3 rounded-lg font-medium">
                  Save Alert
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grades We Support */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Grades We Track</h2>
          <p className="text-slate-600 mb-6">Focused exclusively on junior doctor and resident positions</p>
          <div className="flex flex-wrap justify-center gap-3">
            {['FY1', 'FY2', 'SHO', 'CT1-5', 'ST1-5', 'Clinical Fellow', 'Trust Doctor', 'Specialty Doctor', 'Staff Grade', 'Salaried GP'].map((grade) => (
              <span key={grade} className="px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-sm font-medium">
                {grade}
              </span>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-4">We don't track consultant or senior management positions</p>
        </div>
      </section>

      {/* SEO Section - For IMGs and Junior Doctors */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-medium mb-4">
              Not for Consultants
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Built for Resident Doctors & IMGs
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We understand the struggle of finding your first NHS job. That's why we focus solely on 
              junior doctor positions — no consultant roles cluttering your inbox.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* For IMGs */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">For International Medical Graduates</h3>
              <p className="text-slate-600 mb-4">
                We know how hard it is to land your first UK job. Entry-level positions fill within hours. 
                Our alerts focus on IMG-friendly roles: Trust Grade, Clinical Fellow, SHO, and Staff Grade.
              </p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>PLAB-friendly entry positions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Visa sponsorship opportunities</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>No senior roles to filter through</span>
                </li>
              </ul>
            </div>
            
            {/* For Junior Doctors */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <Stethoscope className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">For Junior & Resident Doctors</h3>
              <p className="text-slate-600 mb-4">
                Foundation doctors, core trainees, specialty trainees — we've got you covered. 
                Get alerts for locum shifts, training posts, and out-of-programme opportunities.
              </p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>FY1, FY2, CT, ST positions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Trust grade & clinical fellow</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Specialty doctor roles</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* What We DON'T Track */}
          <div className="mt-12 p-6 bg-slate-100 rounded-xl">
            <h3 className="text-lg font-semibold text-slate-900 mb-3 text-center">What We Don't Track</h3>
            <p className="text-slate-600 text-center mb-4">
              To keep your alerts focused, we exclude senior positions:
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                'Consultant',
                'Locum Consultant', 
                'Associate Specialist',
                'GP Partner',
                'GP Educator',
                'Specialist Grade',
                'Dental Roles'
              ].map((tag) => (
                <span key={tag} className="px-3 py-1.5 bg-slate-200 text-slate-500 rounded-full text-sm line-through">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
          {/* Common Job Types */}
          <div className="mt-12 text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Roles We Focus On</h3>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                'Foundation Year 1 & 2',
                'SHO / Senior House Officer',
                'Core Training (CT1-5)',
                'Specialty Training (ST1-5)',
                'Clinical Fellow',
                'Trust Grade Doctor',
                'Specialty Doctor',
                'Staff Grade',
                'IMG Entry Positions',
                'NHS Visa Sponsorship'
              ].map((tag) => (
                <span key={tag} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm border border-emerald-100">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white" id="pricing">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Simple Pricing</h2>
            <p className="text-lg text-slate-600">
              Less than the cost of missing out on your dream job.
            </p>
          </div>
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8 lg:p-12 shadow-sm">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-5xl font-bold text-slate-900">£9.99</span>
                  <span className="text-slate-500">/month</span>
                </div>
                <ul className="space-y-3">
                  {[
                    'Unlimited email notifications',
                    'All medical grades & specialties',
                    '24/7 monitoring for new jobs',
                    'Same filters as trac.jobs',
                    'Cancel anytime',
                    'Track up to 5 jobs simultaneously',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="w-full lg:w-auto">
                <Link to="/register">
                  <Button className="w-full lg:w-auto bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-12 py-6 text-lg" data-testid="pricing-cta-btn">
                    Start Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-emerald-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Don't Let Another Job Slip Away
          </h2>
          <p className="text-lg text-emerald-100 mb-8">
            The next perfect job could be posted any moment. Will you know about it?
          </p>
          <Link to="/register">
            <Button className="bg-white text-emerald-600 hover:bg-slate-100 rounded-full px-12 py-6 text-lg font-semibold" data-testid="final-cta-btn">
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-100 text-slate-600 py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-slate-900">Trac Jobs Alert</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/contact" className="text-sm hover:text-slate-900 transition-colors">
                Contact Us
              </Link>
              <Link to="/privacy" className="text-sm hover:text-slate-900 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm hover:text-slate-900 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
          <div className="mt-6 text-center text-sm text-slate-500">
            © 2026 tracjobsalert.co.uk - All rights reserved
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
